import React, { useState, useMemo, useCallback } from 'react';
import { AlertTriangle, DollarSign, Clock, RefreshCw, Building2, User, X, Phone, Mail } from 'lucide-react';

const RentalAlerts = ({ rentals, clients, invoices }) => {
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Helper function to get client details
  const getClientDetails = useCallback((clientId) => {
    return clients?.find(c => c.id === clientId) || {};
  }, [clients]);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Helper function to calculate days overdue
  const getDaysOverdue = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = now - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // 1. Perusahaan yang belum bayar
  const unpaidCorporate = useMemo(() => {
    if (!invoices || !clients) return [];
    return invoices
      .filter(inv => {
        const client = getClientDetails(inv.client_id);
        return (
          inv.status !== 'paid' &&
          client.client_type !== 'individual' &&
          client.client_type !== undefined
        );
      })
      .map(inv => ({
        ...inv,
        client: getClientDetails(inv.client_id),
        daysOverdue: getDaysOverdue(inv.due_date),
        remainingAmount: inv.total_amount - (inv.paid_amount || 0)
      }))
      .sort((a, b) => b.remainingAmount - a.remainingAmount);
  }, [invoices, clients, getClientDetails]);

  // 2. Personal yang belum bayar
  const unpaidIndividual = useMemo(() => {
    if (!invoices || !clients) return [];
    return invoices
      .filter(inv => {
        const client = getClientDetails(inv.client_id);
        return (
          inv.status !== 'paid' &&
          client.client_type === 'individual'
        );
      })
      .map(inv => ({
        ...inv,
        client: getClientDetails(inv.client_id),
        daysOverdue: getDaysOverdue(inv.due_date),
        remainingAmount: inv.total_amount - (inv.paid_amount || 0)
      }))
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
  }, [invoices, clients, getClientDetails]);

  // 3. Rental yang perlu perpanjang (ending soon)
  const needsRenewal = useMemo(() => {
    if (!rentals || !clients) return [];
    const now = new Date();
    const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return rentals
      .filter(rental => {
        const endDate = new Date(rental.end_date);
        return (
          rental.status === 'active' &&
          endDate >= now &&
          endDate <= sevenDaysLater
        );
      })
      .map(rental => ({
        ...rental,
        client: getClientDetails(rental.client_id),
        daysUntilEnd: Math.ceil((new Date(rental.end_date) - now) / (1000 * 60 * 60 * 24))
      }))
      .sort((a, b) => a.daysUntilEnd - b.daysUntilEnd);
  }, [rentals, clients, getClientDetails]);

  // 4. Invoice yang sudah due date (overdue)
  const overdueInvoices = useMemo(() => {
    if (!invoices || !clients) return [];
    return invoices
      .filter(inv => {
        const dueDate = new Date(inv.due_date);
        return inv.status !== 'paid' && dueDate < new Date();
      })
      .map(inv => ({
        ...inv,
        client: getClientDetails(inv.client_id),
        daysOverdue: getDaysOverdue(inv.due_date),
        remainingAmount: inv.total_amount - (inv.paid_amount || 0)
      }))
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
  }, [invoices, clients, getClientDetails]);

  // 5. Tagihan perusahaan yang menjadi hutang (outstanding corporate debts)
  const corporateDebts = useMemo(() => {
    if (!invoices || !clients) return [];
    const debtMap = new Map();
    
    invoices.forEach(inv => {
      const client = getClientDetails(inv.client_id);
      if (client.client_type !== 'individual' && inv.status !== 'paid') {
        const remaining = inv.total_amount - (inv.paid_amount || 0);
        if (remaining > 0) {
          if (debtMap.has(inv.client_id)) {
            const existing = debtMap.get(inv.client_id);
            existing.totalDebt += remaining;
            existing.invoiceCount += 1;
            existing.invoices.push(inv);
          } else {
            debtMap.set(inv.client_id, {
              client: client,
              totalDebt: remaining,
              invoiceCount: 1,
              invoices: [inv]
            });
          }
        }
      }
    });
    
    return Array.from(debtMap.values()).sort((a, b) => b.totalDebt - a.totalDebt);
  }, [invoices, clients, getClientDetails]);

  // Calculate totals
  const totalUnpaidCorporate = unpaidCorporate.reduce((sum, inv) => sum + inv.remainingAmount, 0);
  const totalUnpaidIndividual = unpaidIndividual.reduce((sum, inv) => sum + inv.remainingAmount, 0);
  const totalOverdue = overdueInvoices.reduce((sum, inv) => sum + inv.remainingAmount, 0);
  const totalCorporateDebt = corporateDebts.reduce((sum, debt) => sum + debt.totalDebt, 0);

  const handleAlertClick = (alertType, data) => {
    setSelectedAlert({ type: alertType, data });
    setShowDetailModal(true);
  };

  const AlertCard = ({ title, count, amount, icon, color, onClick, urgency }) => (
    <div 
      onClick={onClick}
      className={`glass-card p-4 sm:p-6 rounded-xl border-2 ${color} cursor-pointer hover:scale-105 transition-all`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-3 rounded-lg ${color.replace('border', 'bg').replace('/40', '/10')}`}>
          {icon}
        </div>
        {urgency && (
          <span className="px-2 py-1 bg-red-500/20 text-red-600 rounded text-xs font-bold animate-pulse">
            {urgency}
          </span>
        )}
      </div>
      <h3 className="text-sm text-muted-foreground mb-2">{title}</h3>
      <p className="text-2xl sm:text-3xl font-bold text-foreground mb-1">{count}</p>
      <p className="text-lg font-semibold text-primary">{formatCurrency(amount)}</p>
      <p className="text-xs text-muted-foreground mt-2">Klik untuk detail</p>
    </div>
  );

  const DetailModal = () => {
    if (!selectedAlert) return null;

    const { type, data } = selectedAlert;

    const renderCorporateUnpaid = () => (
      <div className="space-y-3">
        {unpaidCorporate.map((inv, idx) => (
          <div key={idx} className="bg-muted/30 p-4 rounded-lg border border-border">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <Building2 className="w-4 h-4 text-primary" />
                  <h4 className="font-semibold text-foreground">
                    {inv.client.company_name || inv.client.full_name}
                  </h4>
                </div>
                <p className="text-sm text-muted-foreground">Invoice: {inv.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-red-600">{formatCurrency(inv.remainingAmount)}</p>
                {inv.daysOverdue > 0 && (
                  <span className="text-xs bg-red-500/20 text-red-600 px-2 py-1 rounded">
                    {inv.daysOverdue} hari terlambat
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>
                <p className="text-muted-foreground">Due Date:</p>
                <p className="font-medium text-foreground">{formatDate(inv.due_date)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status:</p>
                <p className={`font-medium ${inv.status === 'overdue' ? 'text-red-600' : 'text-yellow-600'}`}>
                  {inv.status.toUpperCase()}
                </p>
              </div>
            </div>
            <div className="flex space-x-2 border-t border-border pt-3">
              <a
                href={`tel:${inv.client.phone}`}
                className="flex-1 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm flex items-center justify-center space-x-1"
              >
                <Phone className="w-3 h-3" />
                <span>Call</span>
              </a>
              <a
                href={`https://wa.me/${inv.client.phone?.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center justify-center space-x-1"
              >
                <span>💬</span>
                <span>WA</span>
              </a>
              <a
                href={`mailto:${inv.client.email}`}
                className="flex-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm flex items-center justify-center space-x-1"
              >
                <Mail className="w-3 h-3" />
                <span>Email</span>
              </a>
            </div>
          </div>
        ))}
      </div>
    );

    const renderIndividualUnpaid = () => (
      <div className="space-y-3">
        {unpaidIndividual.map((inv, idx) => (
          <div key={idx} className="bg-muted/30 p-4 rounded-lg border border-border">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <User className="w-4 h-4 text-primary" />
                  <h4 className="font-semibold text-foreground">{inv.client.full_name}</h4>
                </div>
                <p className="text-sm text-muted-foreground">Invoice: {inv.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-red-600">{formatCurrency(inv.remainingAmount)}</p>
                {inv.daysOverdue > 0 && (
                  <span className="text-xs bg-red-500/20 text-red-600 px-2 py-1 rounded">
                    {inv.daysOverdue} hari
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>
                <p className="text-muted-foreground">Due Date:</p>
                <p className="font-medium text-foreground">{formatDate(inv.due_date)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Contact:</p>
                <p className="font-medium text-foreground text-xs">{inv.client.phone}</p>
              </div>
            </div>
            <div className="flex space-x-2 border-t border-border pt-3">
              <a
                href={`tel:${inv.client.phone}`}
                className="flex-1 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm flex items-center justify-center"
              >
                <Phone className="w-3 h-3 mr-1" />
                Call
              </a>
              <a
                href={`https://wa.me/${inv.client.phone?.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
              >
                💬 WhatsApp
              </a>
            </div>
          </div>
        ))}
      </div>
    );

    const renderRenewals = () => (
      <div className="space-y-3">
        {needsRenewal.map((rental, idx) => (
          <div key={idx} className="bg-muted/30 p-4 rounded-lg border-2 border-yellow-500/40">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-foreground">{rental.client.full_name || rental.client.company_name}</h4>
                <p className="text-sm text-muted-foreground">Rental ID: {rental.id}</p>
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded font-bold text-sm ${
                  rental.daysUntilEnd <= 2 ? 'bg-red-500/20 text-red-600' : 'bg-yellow-500/20 text-yellow-600'
                }`}>
                  {rental.daysUntilEnd} hari lagi
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>
                <p className="text-muted-foreground">End Date:</p>
                <p className="font-medium text-foreground">{formatDate(rental.end_date)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Total Amount:</p>
                <p className="font-medium text-foreground">{formatCurrency(rental.total_amount)}</p>
              </div>
            </div>
            <div className="bg-blue-500/10 p-3 rounded border border-blue-500/30 mb-3">
              <p className="text-sm text-blue-600">
                <strong>Action Required:</strong> Hubungi client untuk perpanjangan rental
              </p>
            </div>
            <div className="flex space-x-2">
              <a
                href={`tel:${rental.client.phone}`}
                className="flex-1 px-3 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 text-sm text-center"
              >
                📞 Hubungi Sekarang
              </a>
            </div>
          </div>
        ))}
      </div>
    );

    const renderOverdue = () => (
      <div className="space-y-3">
        {overdueInvoices.map((inv, idx) => (
          <div key={idx} className="bg-red-500/10 p-4 rounded-lg border-2 border-red-500/40">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-foreground">
                  {inv.client.company_name || inv.client.full_name}
                </h4>
                <p className="text-sm text-muted-foreground">Invoice: {inv.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-red-600">{formatCurrency(inv.remainingAmount)}</p>
                <span className="text-xs bg-red-600 text-white px-2 py-1 rounded font-bold">
                  OVERDUE {inv.daysOverdue} HARI
                </span>
              </div>
            </div>
            <div className="bg-red-600/20 p-3 rounded mb-3">
              <p className="text-sm text-red-700 font-semibold">
                ⚠️ URGENT: Tagihan sudah melewati due date! Segera lakukan penagihan.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs mb-3">
              <div>
                <p className="text-muted-foreground">Due Date:</p>
                <p className="font-medium text-foreground">{formatDate(inv.due_date)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Total:</p>
                <p className="font-medium text-foreground">{formatCurrency(inv.total_amount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Paid:</p>
                <p className="font-medium text-green-600">{formatCurrency(inv.paid_amount || 0)}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <a
                href={`tel:${inv.client.phone}`}
                className="flex-1 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm text-center font-semibold"
              >
                📞 TAGIH SEKARANG
              </a>
            </div>
          </div>
        ))}
      </div>
    );

    const renderCorporateDebts = () => (
      <div className="space-y-3">
        {corporateDebts.map((debt, idx) => (
          <div key={idx} className="bg-muted/30 p-4 rounded-lg border-2 border-orange-500/40">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <Building2 className="w-5 h-5 text-orange-500" />
                  <h4 className="font-semibold text-foreground text-lg">
                    {debt.client.company_name}
                  </h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  {debt.invoiceCount} invoice belum lunas
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(debt.totalDebt)}</p>
                <span className="text-xs bg-orange-500/20 text-orange-600 px-2 py-1 rounded">
                  Total Hutang
                </span>
              </div>
            </div>
            <div className="bg-orange-500/10 p-3 rounded mb-3">
              <p className="text-sm text-orange-700 font-medium">
                📊 Detail {debt.invoiceCount} Invoice:
              </p>
              <div className="mt-2 space-y-1">
                {debt.invoices.map((inv, iidx) => (
                  <div key={iidx} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{inv.invoice_number}</span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(inv.total_amount - (inv.paid_amount || 0))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>
                <p className="text-muted-foreground">Contact Person:</p>
                <p className="font-medium text-foreground">{debt.client.contact_person || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Phone:</p>
                <p className="font-medium text-foreground">{debt.client.phone}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <a
                href={`tel:${debt.client.phone}`}
                className="flex-1 px-3 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm text-center font-semibold"
              >
                📞 Tagih Hutang
              </a>
              <a
                href={`mailto:${debt.client.email}`}
                className="flex-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm text-center"
              >
                📧 Email Reminder
              </a>
            </div>
          </div>
        ))}
      </div>
    );

    const modalTitles = {
      corporate: '🏢 Perusahaan Belum Bayar',
      individual: '👤 Personal Belum Bayar',
      renewal: '🔄 Perlu Perpanjangan',
      overdue: '⚠️ Invoice Overdue',
      debt: '💰 Hutang Perusahaan'
    };

    const modalContent = {
      corporate: renderCorporateUnpaid,
      individual: renderIndividualUnpaid,
      renewal: renderRenewals,
      overdue: renderOverdue,
      debt: renderCorporateDebts
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="glass-card p-6 rounded-xl border border-border/40 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6 sticky top-0 bg-background/95 backdrop-blur-sm pb-4 border-b border-border">
            <h3 className="text-2xl font-bold text-foreground">{modalTitles[type]}</h3>
            <button
              onClick={() => setShowDetailModal(false)}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {modalContent[type]()}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 mb-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center">
            <AlertTriangle className="w-6 h-6 mr-2 text-red-500" />
            Alert & Notifikasi - Decision Dashboard
          </h2>
          <p className="text-sm text-muted-foreground">
            Klik pada setiap card untuk melihat detail lengkap dan mengambil action
          </p>
        </div>
      </div>

      {/* Alert Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Perusahaan Belum Bayar */}
        <AlertCard
          title="🏢 Perusahaan Belum Bayar"
          count={unpaidCorporate.length}
          amount={totalUnpaidCorporate}
          icon={<Building2 className="w-6 h-6 text-blue-600" />}
          color="border-blue-500/40 hover:border-blue-500"
          onClick={() => handleAlertClick('corporate', unpaidCorporate)}
          urgency={unpaidCorporate.length > 5 ? 'HIGH' : null}
        />

        {/* Personal Belum Bayar */}
        <AlertCard
          title="👤 Personal Belum Bayar"
          count={unpaidIndividual.length}
          amount={totalUnpaidIndividual}
          icon={<User className="w-6 h-6 text-purple-600" />}
          color="border-purple-500/40 hover:border-purple-500"
          onClick={() => handleAlertClick('individual', unpaidIndividual)}
        />

        {/* Perlu Perpanjangan */}
        <AlertCard
          title="🔄 Perlu Perpanjangan"
          count={needsRenewal.length}
          amount={needsRenewal.reduce((sum, r) => sum + r.total_amount, 0)}
          icon={<RefreshCw className="w-6 h-6 text-yellow-600" />}
          color="border-yellow-500/40 hover:border-yellow-500"
          onClick={() => handleAlertClick('renewal', needsRenewal)}
          urgency={needsRenewal.some(r => r.daysUntilEnd <= 2) ? 'URGENT' : null}
        />

        {/* Invoice Overdue */}
        <AlertCard
          title="⚠️ Invoice Overdue"
          count={overdueInvoices.length}
          amount={totalOverdue}
          icon={<Clock className="w-6 h-6 text-red-600" />}
          color="border-red-500/40 hover:border-red-500"
          onClick={() => handleAlertClick('overdue', overdueInvoices)}
          urgency={overdueInvoices.length > 0 ? 'CRITICAL' : null}
        />

        {/* Hutang Perusahaan */}
        <AlertCard
          title="💰 Hutang Perusahaan"
          count={corporateDebts.length}
          amount={totalCorporateDebt}
          icon={<DollarSign className="w-6 h-6 text-orange-600" />}
          color="border-orange-500/40 hover:border-orange-500"
          onClick={() => handleAlertClick('debt', corporateDebts)}
          urgency={totalCorporateDebt > 50000000 ? 'HIGH' : null}
        />

        {/* Summary Card */}
        <div className="glass-card p-6 rounded-xl border-2 border-primary/40 bg-primary/5">
          <h3 className="text-sm text-muted-foreground mb-3">📊 Total Outstanding</h3>
          <p className="text-3xl font-bold text-foreground mb-2">
            {formatCurrency(totalUnpaidCorporate + totalUnpaidIndividual + totalOverdue)}
          </p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Items:</span>
              <span className="font-medium text-foreground">
                {unpaidCorporate.length + unpaidIndividual.length + overdueInvoices.length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Critical:</span>
              <span className="font-medium text-red-600">{overdueInvoices.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="glass-card p-4 rounded-xl border border-blue-500/40 bg-blue-500/5">
        <p className="text-sm text-foreground">
          💡 <strong>Decision Guide:</strong> Prioritaskan invoice overdue (merah) terlebih dahulu, 
          lalu fokus ke perpanjangan rental yang akan berakhir dalam 2 hari. 
          Untuk hutang perusahaan besar, jadwalkan meeting follow-up.
        </p>
      </div>

      {/* Detail Modal */}
      {showDetailModal && <DetailModal />}
    </div>
  );
};

export default RentalAlerts;
